// Where the app is: the address, and moving it.
//
// One module owns the browser's history, so that no screen has to get it
// right on its own. A move is one of three kinds:
//
//   navigate(path)       forward: a new entry, which Back returns from;
//   replaceWith(path)    this entry becomes another screen: a form saved, a
//                        thing deleted — Back does not come back to it;
//   goBack(parent)       Back, as the browser's button would go, or — with
//                        nothing of ours behind the page, a link opened cold —
//                        the parent in this page's place.
//
// And a screen's own state that should survive a reload, a Back into it or
// a link sent to another computer is written into the address with
// setAddress (read with useAddress), never assembled by hand.
//
// Every entry this app makes says how many came before it and what screen it
// was made from, which is what lets Back mean back and name where it goes,
// and where the page was scrolled to, which Back and Forward restore once the
// screen has drawn enough to scroll that far. A forward move starts at the top.

import { useMemo, useSyncExternalStore } from "react";
import { checkBuild, staleBuild } from "./api.ts";

export type How = "start" | "push" | "replace" | "pop" | "self";

export interface Place {
  path: string;
  search: string;
  /** Changes when the screen should look at the address again: a navigation,
   *  or Back and Forward. Not when a screen writes its own place into it. */
  moved: number;
  /** How the app came to be here. */
  how: How;
}

interface HistoryState {
  n?: number;
  from?: string;
  scroll?: number;
}

const clean = (path: string) => path.replace(/\/+$/, "") || "/";

let place: Place = { path: clean(location.pathname), search: location.search, moved: 0, how: "start" };
const listeners = new Set<() => void>();

function update(how: How): void {
  const moved = how !== "self";
  place = { path: clean(location.pathname), search: location.search, moved: place.moved + (moved ? 1 : 0), how: moved ? how : place.how };
  for (const listener of listeners) listener();
}

// The app puts the page back where it was itself: the browser would do it
// before the screen has anything to scroll through.
if ("scrollRestoration" in history) history.scrollRestoration = "manual";

window.addEventListener("popstate", () => {
  update("pop");
  restoreScroll(historyState().scroll || 0);
});

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePlace = (): Place => useSyncExternalStore(subscribe, () => place);
export const currentPlace = (): Place => place;

/** The title of the screen on show, for the history entries it makes. */
let screenTitle = "";
export function setScreenTitle(title: string): void {
  screenTitle = title;
}

const historyState = (): HistoryState => (history.state as HistoryState | null) || {};

/** Write into this entry without adding one. Safari allows a hundred of these
 *  in thirty seconds and throws past that; a lost scroll is not worth an error. */
function writeState(state: HistoryState, url?: string): void {
  try {
    history.replaceState(state, "", url);
  } catch {
    // Too many too quickly: the next one will do.
  }
}

// --- SCROLL, PER ENTRY ---

/** Where this entry's page is scrolled to, kept in the entry. */
function saveScroll(): void {
  const scroll = Math.round(window.scrollY);
  const state = historyState();
  if (state.scroll !== scroll) writeState({ ...state, scroll });
}

let saveTimer = 0;
window.addEventListener(
  "scroll",
  () => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(saveScroll, 200);
  },
  { passive: true }
);

// A list arrives from the server after its screen is up, so the page may not
// be long enough yet to scroll back to where it was: keep trying until it is,
// for a few seconds, unless the person scrolls first.
let restoring = 0;
function restoreScroll(target: number): void {
  const token = ++restoring;
  const until = performance.now() + 3000;
  const step = () => {
    if (token !== restoring) return;
    window.scrollTo(0, target);
    if (Math.abs(window.scrollY - target) < 2 || performance.now() > until) return;
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
const stopRestoring = () => void restoring++;
window.addEventListener("wheel", stopRestoring, { passive: true });
window.addEventListener("touchstart", stopRestoring, { passive: true });

// --- MOVES ---

function stepOn(): HistoryState {
  return { n: (historyState().n || 0) + 1, from: screenTitle };
}

/** Forward: a new entry, which Back returns from. */
export function navigate(path: string): void {
  saveScroll();
  stopRestoring();
  // Deployed since this page loaded: take the new code now, on a move that
  // was going to replace the screen anyway.
  if (staleBuild) {
    location.assign(path);
    return;
  }
  history.pushState(stepOn(), "", path);
  update("push");
  void checkBuild();
}

/** This entry becomes another screen: what it was — a form that is saved, a
 *  thing that is deleted — is not somewhere Back should lead. */
export function replaceWith(path: string): void {
  stopRestoring();
  if (staleBuild) {
    location.replace(path);
    return;
  }
  const { n, from } = historyState();
  writeState({ n, from }, path);
  update("replace");
  void checkBuild();
}

/** Back, as the browser's button would go, or to `parent` when nothing of
 *  ours is behind this page. The parent takes this page's place rather than
 *  going on top of it: pushed, it would have this page behind it, and its own
 *  Back would come straight back here — a drill opened cold and its
 *  repertoire, each Back to the other, for ever. */
export function goBack(parent: string): void {
  saveScroll();
  if ((historyState().n || 0) > 0) history.back();
  else replaceWith(parent);
}

/** What Back is called: the screen it returns to. */
export function backLabel(parentLabel: string): string {
  const state = historyState();
  if ((state.n || 0) > 0 && state.from) return state.from;
  return parentLabel;
}

// --- THE SCREEN'S OWN STATE, IN THE ADDRESS ---

export type AddressChanges = Record<string, string | number | null | undefined | false>;

/** A value as the address shows it: readable where it can be — the commas of
 *  a list of moves, the colons of a puzzle's name — escaped where it must. */
const encode = (value: string) => encodeURIComponent(value).replace(/%2C/gi, ",").replace(/%3A/gi, ":");

/**
 * Put a screen's own state into the address: each key set to its value, or
 * taken out when the value is empty; keys not named are left as they are.
 * A reload, Back into the screen, or the link opened on another computer
 * then comes back to it. `push` makes it a step that Back returns to — a
 * puzzle of the queue — rather than the same step, changed.
 */
export function setAddress(changes: AddressChanges, { push = false } = {}): void {
  const params = new URLSearchParams(location.search);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined || value === false || value === "") params.delete(key);
    else params.set(key, String(value));
  }
  const search = [...params].map(([k, v]) => `${encode(k)}=${encode(v)}`).join("&");
  const url = location.pathname + (search ? "?" + search : "");
  if (url === location.pathname + location.search) return;
  if (push) {
    saveScroll();
    history.pushState(stepOn(), "", url);
  } else writeState(historyState(), url);
  update("self");
}

/** The address's parameters, read again when Back or Forward changes them. */
export function useAddress(): URLSearchParams {
  const { search } = usePlace();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/** A link inside the app: a real link, followed without reloading. */
export function follow(path: string) {
  return {
    href: path,
    onClick: (e: { preventDefault(): void; metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      navigate(path);
    },
  };
}
